/**
 * Premium Button Control Components
 *
 * Individual premium button controls that can be completely replaced by pro plugins
 * using WordPress hooks and filters. Pro plugins can replace the entire component
 * implementation by hooking into the component-level filters.
 *
 * Each component applies a filter to the entire component return, allowing pro plugins
 * to replace them completely with their own implementation.
 *
 * Available Filters:
 * - redfox_thank_you_button_gradient_control
 * - redfox_thank_you_button_box_shadow_control
 * - redfox_thank_you_button_spacing_control
 * - redfox_thank_you_button_typography_control
 * - redfox_thank_you_button_border_control
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import {
	BaseControl,
	GradientPicker,
} from '@redfox-thank-you/controls';
import { applyFilters } from '@wordpress/hooks';
import { __ } from '@wordpress/i18n';
import React from 'react';

import type { ButtonAttributes, ButtonValues, ResponsiveBreakpoint } from './types';

/**
 * Interface for individual button premium control props
 */
export interface ButtonPremiumControlComponentProps {
	attributes: ButtonAttributes;
	setAttributes: ( attrs: Partial< ButtonAttributes > ) => void;
	activeBreakpoint: ResponsiveBreakpoint;
	currentValues: ButtonValues;
	updateAttribute: (
		key: string
	) => ( value: string | number | Record< string, unknown > | undefined ) => void;
	resetProperty: ( property: string ) => void;
	hasPropertyValue: ( property: string ) => boolean;
	showIndividualResets?: boolean;
}

/**
 * Gradient Background Control Component
 *
 * @param props - The component props
 * @return JSX element for gradient background control
 */
const GradientBackgroundControl: React.FC< ButtonPremiumControlComponentProps > = (
	props,
) => {
	const { currentValues } = props;

	// Default component implementation (Pro placeholder)
	const defaultComponent = (
		<BaseControl
			id="button-gradient-background"
			label={ __( 'Gradient Background', 'redfox-thank-you' ) }
			isPro
		>
			<GradientPicker
				value={ currentValues.gradientBackground }
				onChange={ () => {} }
			/>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_button_gradient_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Box Shadow Control Component
 *
 * @param props - The component props
 * @return JSX element for box shadow control
 */
const BoxShadowControl: React.FC< ButtonPremiumControlComponentProps > = (
	props,
) => {
	// Default component implementation (Pro placeholder)
	const defaultComponent = (
		<BaseControl
			id="button-box-shadow"
			label={ __( 'Box Shadow', 'redfox-thank-you' ) }
			isPro
		>
			<div className="redfox-thank-you-button-control__pro-placeholder">
				{ __( 'Box Shadow controls available in Pro', 'redfox-thank-you' ) }
			</div>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_button_box_shadow_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Spacing Control Component (Padding and Margin)
 *
 * @param props - The component props
 * @return JSX element for spacing control
 */
const SpacingControl: React.FC< ButtonPremiumControlComponentProps > = (
	props,
) => {
	// Default component implementation (Pro placeholder)
	const defaultComponent = (
		<BaseControl
			id="button-spacing"
			label={ __( 'Spacing', 'redfox-thank-you' ) }
			isPro
		>
			<div className="redfox-thank-you-button-control__pro-placeholder">
				{ __( 'Spacing controls available in Pro', 'redfox-thank-you' ) }
			</div>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_button_spacing_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Typography Control Component
 *
 * @param props - The component props
 * @return JSX element for typography control
 */
const TypographyControl: React.FC< ButtonPremiumControlComponentProps > = (
	props,
) => {
	// Default component implementation (Pro placeholder)
	const defaultComponent = (
		<BaseControl
			id="button-typography"
			label={ __( 'Typography', 'redfox-thank-you' ) }
			isPro
		>
			<div className="redfox-thank-you-button-control__pro-placeholder">
				{ __( 'Typography controls available in Pro', 'redfox-thank-you' ) }
			</div>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_button_typography_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Border Control Component
 *
 * @param props - The component props
 * @return JSX element for border control
 */
const BorderControl: React.FC< ButtonPremiumControlComponentProps > = (
	props,
) => {
	// Default component implementation (Pro placeholder)
	const defaultComponent = (
		<BaseControl
			id="button-border"
			label={ __( 'Border', 'redfox-thank-you' ) }
			isPro
		>
			<div className="redfox-thank-you-button-control__pro-placeholder">
				{ __( 'Border controls available in Pro', 'redfox-thank-you' ) }
			</div>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_button_border_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Export all premium button controls
 */
export {
	BorderControl as ButtonBorderControl,
	BoxShadowControl as ButtonBoxShadowControl,
	GradientBackgroundControl as ButtonGradientBackgroundControl,
	SpacingControl as ButtonSpacingControl,
	TypographyControl as ButtonTypographyControl,
};

